import React, { useState, useEffect, useRef, RefObject } from "react";
import styled, { css } from "styled-components";
import { DropdownHandler } from "../DropdownHandler";
import { Dropdown, DropdownContainer, DropdownElement, Option, OptionText } from "../SelectMenu";
import { UserInputTag, TagList } from "../Tag";
import { InputWrapper, Wrapper, Label } from "../TextInputUtils";
import { Tooltip } from "../Tooltip";
import { KEY_ENTER, KEY_COMMA, KEY_SPACE, KEY_TAB, KEY_BACKSPACE, KEY_ARROW_DOWN } from "../constants/keyboardCodes";
import { useDebounce } from "../hooks/useDebounce";
import { useKeyboardNav } from "../hooks/useKeyboardNav";
import { usePortalDropdownCoords } from "../hooks/usePortalDropdownCoords";
import { style } from "../style";

const VALID_TAG_REGEX = /^[\w\-:]{1,255}$/;
const DEBOUNCE_DELAY = 250;

export type TagEditorProps = React.HTMLAttributes<HTMLInputElement> & {
  onTagInput: (updatedTags: string[]) => void;
  onDebouncedInput?: (value: string) => void;
  onAddTags?: (tag: string, currentTags: string[]) => string[] | undefined;
  validateInput?: (input: string) => boolean;
  name?: string;
  placeholder?: string;
  suggestions?: string[];
  mobileTitle?: string;
  initialTags?: string[];
  textarea?: boolean;
  hideTagIcon?: boolean;
  label?: string;
  labelTooltip?: string;
  description?: string;
  focusSuggestions?: boolean;
};

type EditorProps = {
  $focus: boolean;
  $textarea: boolean;
};

const InvisibleInput = styled.input`
  appearance: none;
  border: none !important;
  &:active,
  &:focus {
    box-shadow: none !important;
  }
`;

const TagsAutocompleteDropdown = styled(DropdownElement)`
  box-shadow: ${style.vars.boxShadow.base};
`;

const Editor = styled.div<EditorProps>`
  display: flex;
  align-items: center;
  width: auto;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
  border-radius: ${style.vars.borderRadius.base};

  &:hover {
    cursor: text;
  }

  ${({ $textarea }) =>
    $textarea &&
    css`
      min-height: 200px;
      max-width: 100%;
      align-items: flex-start;
      padding: ${style.vars.space["3"]};
      flex-direction: column;

      ${TagList} {
        max-width: 100%;
      }

      ${InvisibleInput} {
        padding-left: 0;
      }
    `}

  ${({ $focus }) =>
    $focus &&
    css`
      border-color: ${style.colors.blue.dark};
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
`;

const normalizeTagInput = (tag = ""): string => {
  return tag.trim().replace(",", "");
};

/**
 * Displays an input that allows tags to be added and removed.
 *
 * On each update to the list of added tags the `onTagInput`
 * callback is invoked with the new list of tags. Optionally
 * a `onDebouncedInput` callback can be passed and will be
 * invoked with the current entered value with a delay, which
 * allows async operations such as fetching a list of
 * suggested tags to be made.
 *
 * A list of suggestions can be passed to the `suggestions` prop
 * which will render an accessible dropdown list allowing users
 * to select a suggested item.
 */
export const TagEditor: React.FC<TagEditorProps> = ({
  focusSuggestions = false,
  textarea = false,
  hideTagIcon = false,
  suggestions = [],
  name = "tags",
  placeholder,
  mobileTitle,
  initialTags,
  label,
  labelTooltip,
  description,
  onTagInput,
  onDebouncedInput,
  validateInput,
  onAddTags,
  id,
  ...inputProps
}) => {
  const [addedTags, setAddedTags] = useState<string[]>(initialTags ?? []);
  const [currentInputValue, setCurrentInputValue] = useState("");
  const debouncedInputValue = useDebounce(currentInputValue, DEBOUNCE_DELAY);
  const [shouldFocusEditor, setShouldFocusEditor] = useState(false);
  const dropdownElementRef = useRef<HTMLDivElement>();
  const inputId = id || name;

  const { isMenuOpen, menuRef, inputRef, onKeyUp, onComboBoxKeyDown, onItemKeyUp, open, close, rover } = useKeyboardNav({
    isMenuOpen: false,
  });
  const { coords, updateCoords, shouldUseCoords } = usePortalDropdownCoords({
    menuRef,
    isMenuOpen,
    renderInPortal: false,
  });

  // filtered list of suggestions that have not been added yet
  const unSelectedSuggestions = suggestions.filter((suggestion) => {
    return !addedTags.includes(suggestion);
  });

  // Updates the list of added tags by removing the `selectedTag`.
  // If no `selectedTag` is provided the last tag in the list is removed.
  const removeTag = (selectedTag?: string): void => {
    setAddedTags((currentTags) => {
      const tagToRemove = selectedTag ?? currentTags[currentTags.length - 1];
      return currentTags.filter((tag) => tag !== tagToRemove);
    });
  };

  const addTag = (tag: string): void => {
    setAddedTags((currentTags) => {
      const inputTag = normalizeTagInput(tag);

      // Validate tag before inserting
      if (
        currentTags.includes(inputTag) ||
        !VALID_TAG_REGEX.test(inputTag) ||
        // invoke validate function if available from consumer
        (validateInput && !validateInput(inputTag))
      ) {
        return currentTags;
      }

      let newTags = [...currentTags, inputTag];

      if (onAddTags) {
        const potentialNewTags = onAddTags(inputTag, newTags);
        newTags = potentialNewTags ? potentialNewTags : newTags;
      }

      setCurrentInputValue("");
      return Array.from(new Set(newTags));
    });
  };

  const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if ([KEY_ENTER, KEY_COMMA, KEY_SPACE, KEY_TAB].indexOf(event.key) !== -1) {
      event.preventDefault();
      addTag(currentInputValue);
    }

    if (!currentInputValue && KEY_BACKSPACE === event.key) {
      event.preventDefault();
    }
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    onComboBoxKeyDown(event);
    open();

    // allows users to navigate via the arrow down key to the tags list
    if (event.key == KEY_ARROW_DOWN && unSelectedSuggestions.length > 0) {
      const firstOption = dropdownElementRef?.current?.firstElementChild as HTMLElement;
      firstOption?.focus();
    }

    if (!currentInputValue && KEY_BACKSPACE === event.key) {
      removeTag();
    }
  };

  useEffect(() => {
    onTagInput(addedTags);
  }, [addedTags]);

  useEffect(() => {
    onDebouncedInput && onDebouncedInput(debouncedInputValue);
  }, [debouncedInputValue]);

  return (
    <Wrapper>
      <Label htmlFor={inputId}>
        {label}
        {labelTooltip && <Tooltip.Help content={labelTooltip} />}
      </Label>
      <div>{description}</div>
      <InputWrapper>
        <Editor $focus={shouldFocusEditor || isMenuOpen} $textarea={textarea} onClick={() => inputRef?.current?.focus()}>
          <TagList>
            {addedTags.map((tag: string) => (
              <UserInputTag key={tag} onDelete={removeTag} hideTagIcon={hideTagIcon} rover={rover}>
                {tag}
              </UserInputTag>
            ))}
          </TagList>
          <InvisibleInput
            id={inputId}
            placeholder={placeholder}
            name={name}
            ref={inputRef as RefObject<HTMLInputElement>}
            value={currentInputValue}
            onChange={(e) => setCurrentInputValue(e.currentTarget.value)}
            onKeyPress={handleOnKeyPress}
            onKeyDown={handleOnKeyDown}
            onKeyUp={onKeyUp}
            onFocus={() => {
              focusSuggestions && open();
              setShouldFocusEditor(true);
            }}
            onBlur={() => setShouldFocusEditor(false)}
            {...inputProps}
          />
        </Editor>
      </InputWrapper>
      {unSelectedSuggestions.length > 0 && (
        <DropdownContainer>
          <DropdownHandler
            mobileTitle={mobileTitle}
            mobileAlignment="fixed-bottom"
            clickAway={(e) => {
              !shouldFocusEditor && close();
              e.stopPropagation();
            }}
            inProp={isMenuOpen}
            renderInPortal={false}
          >
            <Dropdown coords={coords} updateCoords={updateCoords} shouldUseCoords={shouldUseCoords}>
              <TagsAutocompleteDropdown ref={dropdownElementRef as RefObject<HTMLDivElement>}>
                {unSelectedSuggestions.map((suggestion) => (
                  <Option role="menuitem" key={suggestion} onKeyUp={onItemKeyUp} onClick={() => addTag(suggestion)} {...rover}>
                    <OptionText>{suggestion}</OptionText>
                  </Option>
                ))}
              </TagsAutocompleteDropdown>
            </Dropdown>
          </DropdownHandler>
        </DropdownContainer>
      )}
    </Wrapper>
  );
};
